import { filterClasses } from "@swivl-mono/helpers";
import { ReactNode, useState } from "react";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";

import { formatTime } from "../Helpers/formatTime";

/** Heading above a block inside an expanded row. */
export const SECTION_LABEL = "body-sm text-grey-600 uppercase";

/** The pill itself — Figma "Chat Invisibles" (node 72:7407). */
const PILL = "body-mono inline-flex items-center gap-2 rounded-sm px-2 py-1 text-grey-600";

/**
 * Pill fill. A dedicated prop rather than a `className` override because
 * `filterClasses` only concatenates — two background classes would resolve by
 * stylesheet order, not by which one was passed last.
 */
const PILL_TONE = {
  /** Default internal row. */
  grey: "bg-grey-50",
  /** Rows about a human joining or a ticket — matches `TakeoverEventNotice`. */
  teal: "bg-teal-50",
  /** Admin-only rows: the machine internals a regular agent never sees. */
  purple: "bg-purple-50"
} as const;

/** Hover fill, one step darker than the tone's own. */
const PILL_TONE_HOVER = {
  grey: "hover:bg-grey-100",
  teal: "hover:bg-teal-100",
  purple: "hover:bg-purple-100"
} as const;

interface InvisibleRowProps {
  /** 16px glyph, inherits the pill's grey-600 via currentColor. */
  icon: ReactNode;
  label: ReactNode;
  /** Appended after the label as "• 2:02 PM" when present. */
  createdAt?: string;
  /** Detail revealed on click. Omit for a row that isn't expandable. */
  children?: ReactNode;
  /** Rendered inside the pill after the label — e.g. the ticket chip. */
  trailing?: ReactNode;
  /** Fired when the row opens — the LLM card uses it to lazy-load its record. */
  onExpand?: () => void;
  /** Pill fill. Defaults to grey; `teal` marks rows about a person or a ticket. */
  tone?: keyof typeof PILL_TONE;
}

/**
 * One internal ("invisible") timeline row: the things the customer never saw —
 * triggers, tool and LLM calls, routing and escalation events, navigation.
 *
 * Styled from Figma "Chat Invisibles": a centred grey-50 pill with a 4px
 * radius, a 16px leading icon, and a 12/16 mono label in grey-600 followed by a
 * wall-clock time. Rows carrying detail expand in place rather than printing it
 * inline, so a long transcript stays scannable.
 *
 * Only ever rendered inside `ConversationTimeline`, which is studio-only — the
 * widget reaches chat-ui through `ChatWindow` → `MessageList` instead. That's
 * why `body-mono` is safe here; anything on the widget's path must stay on raw
 * Tailwind, since the widget never loads `libs/ui/styles.css`.
 */
export function InvisibleRow({ icon, label, createdAt, children, trailing, onExpand, tone = "grey" }: InvisibleRowProps): React.ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const time = createdAt ? formatTime(createdAt) : "";
  const isExpandable = Boolean(children);

  const handleToggle = (): void => {
    setIsExpanded((prev) => {
      if (!prev) onExpand?.();
      return !prev;
    });
  };

  const inner = (
    <>
      <span className="flex size-4 shrink-0 items-center justify-center">{icon}</span>
      {/* 408px cap comes from the Figma node — without it a row carrying a full
          sentence (the system hand-back) stretches the pill across the panel. */}
      <span className="max-w-102 text-left">
        {label}
        {time ? ` • ${time}` : ""}
      </span>
      {trailing}
      {isExpandable && (isExpanded ? <IoChevronUp size={12} className="shrink-0" /> : <IoChevronDown size={12} className="shrink-0" />)}
    </>
  );

  return (
    <div className="flex flex-col items-center">
      {isExpandable ? (
        <button
          type="button"
          aria-expanded={isExpanded}
          onClick={handleToggle}
          className={filterClasses(PILL, PILL_TONE[tone], PILL_TONE_HOVER[tone], "cursor-pointer transition-colors")}
        >
          {inner}
        </button>
      ) : (
        <div className={filterClasses(PILL, PILL_TONE[tone])}>{inner}</div>
      )}
      {/* Detail is centred under the pill and capped, so it reads as belonging
          to the row rather than to the transcript's left edge. `CodeBlock`
          re-asserts `text-left` for itself — centred JSON is unreadable. */}
      {isExpanded && children && <div className="mt-1 w-full max-w-lg px-2 text-center">{children}</div>}
    </div>
  );
}
